<script>
    import { onMount } from 'svelte';
    import { createEventDispatcher } from 'svelte';
    import { Tooltip } from 'sveltestrap';

    import { cohortFilters, reportDate, vptServer, inmateCount, userUrn, userFilters, token, userName } from '../stores.js';
    import { config } from '../config.js';
    import { referenceDataKeyworkerList, referenceDataStatusList, referenceDataEthnicityList, referenceDataNationalityList,
        referenceDataReligionList, referenceDataUnitList, referenceDataWingList, referenceDataCellStatusList,
        referenceDataRegimeLevelList, referenceDataAgeRangeList } from "../reference-data";
    import { getApiDateFormat } from "../date-time";
    import { createRequestParams, toProperCase } from '../string.js';

    import AimToast from '../component/AimToast.svelte';
    import FilterButton from "../component/FilterButton.svelte";
    import SelectableList from '../component/SelectableList.svelte';

    import DatePicker from './DatePicker.svelte';
    import Config from './Config.svelte';
    import Help from './Help.svelte';

    const dispatch = createEventDispatcher();

    export let areListsLoading; 

    let requestProperties;
    let requestParams;

    let selectedDate = ($reportDate) ? $reportDate : new Date();
    
    let keyworkerFilterString = "";
    let statusFilterString = "";
    let ethnicityFilterString = "";
    let nationalityFilterString = "";
    let religionFilterString = "";
    let unitFilterString = "";
    let wingFilterString = "";
    let cellStatusFilterString = "";
    let regimeFilterString = "";
    let ageRangeFilterString = "";
    let earlyDaysFilter = false;

    let toastProperties = null; 
    let showFavouriteTooltips = true;

    onMount(() => {
        getReferenceDataKeyworkersOnDate();
    });

    $: {
        if (!requestParams && $cohortFilters) {
            keyworkerFilterString = $cohortFilters.find(e => e.name == "staffFilter").value;
            statusFilterString = $cohortFilters.find(e => e.name == "statusFilter").value;
            ethnicityFilterString = $cohortFilters.find(e => e.name == "ethnicFilter").value;
            nationalityFilterString = $cohortFilters.find(e => e.name == "nationalityFilter").value;
            religionFilterString = $cohortFilters.find(e => e.name == "religionFilter").value;
            unitFilterString = $cohortFilters.find(e => e.name == "unitFilter").value;
            wingFilterString = $cohortFilters.find(e => e.name == "wingFilter").value;
            cellStatusFilterString = $cohortFilters.find(e => e.name == "cellStatusFilter").value;
            regimeFilterString = $cohortFilters.find(e => e.name == "regimeFilter").value;
            ageRangeFilterString = $cohortFilters.find(e => e.name == "ageRangeFilter").value;
            earlyDaysFilter = $cohortFilters.find(e => e.name == "earlyDaysFilter").value == 'Y' ? true : false;
        }

        requestParams = [
                { name: "staffFilter", value: keyworkerFilterString },
                { name: "statusFilter", value: statusFilterString },
                { name: "ethnicFilter", value: ethnicityFilterString },
                { name: "nationalityFilter", value: nationalityFilterString },
                { name: "religionFilter", value: religionFilterString },
                { name: "unitFilter", value: unitFilterString },
                { name: "wingFilter", value: wingFilterString },
                { name: "cellStatusFilter", value: cellStatusFilterString },
                { name: "regimeFilter", value: regimeFilterString },
                { name: "ageRangeFilter", value: ageRangeFilterString },
                { name: "earlyDaysFilter", value: earlyDaysFilter ? 'Y' : ''}
        ];
        
        cohortFilters.set(requestParams);

        requestProperties = {
            requestDate: getApiDateFormat($reportDate),
            requestParams: requestParams
        }

        dispatch('update', { requestProperties: requestProperties });
    }

    function selectReportDate(d) {
        selectedDate = d.detail;
        getReferenceDataKeyworkersOnDate();
	}

    async function getReferenceDataKeyworkersOnDate() {
        await fetch(`${$vptServer}/reference-data/staff/${getApiDateFormat(selectedDate)}`)
        .then(r => {
            if (!r.ok) {
                throw Error(r.status);
            }
            return r;
        })
        .then(r => r.json())
        .then(data => {
            // After keyworker filter list is refreshed, add checked back into those that were already selected
            let checkedKeyworkers = $referenceDataKeyworkerList.filter(e => e.checked).map(e => {return e.staffUrn});

            let refreshedKeyworkerList;
            if (data && data.length > 0) {
                refreshedKeyworkerList = data.map(e => {
                    if (checkedKeyworkers.includes(e.staffUrn)) {
                        return {...e, checked: true};
                    } else {
                        return e;
                    }
                });
            }

            referenceDataKeyworkerList.set(refreshedKeyworkerList);
            reportDate.set(selectedDate);
        })
        .catch(error => {
            console.error(error);
            return;
        });
    }

    function saveUserFilters(event) {
        event.preventDefault();
        showFavouriteTooltips = false;
        setTimeout(() => {
            showFavouriteTooltips = true;
        }, "1000");
        fetch(`${$vptServer}/user/${$userUrn}/configuration/update`, {
            method:  'PUT',
            headers: {
                "Authorization": "Bearer " + $token,
                'Content-Type': 'text/plain'
            },
            body: createRequestParams(requestParams)
        });

        if (requestParams.findIndex( p => p.value != "") == -1) {
            toastProperties = {
                title: toProperCase($userName) + "'s Dashboard",
                content: "Default view restored to the whole establishment",        
                isOpen: true
            }
        }

        userFilters.set(createRequestParams(requestParams));
    }

    function clearUserFilters(event) {
        event.preventDefault();
        showFavouriteTooltips = false;
        setTimeout(() => {
            showFavouriteTooltips = true;
        }, "1000");
        fetch(`${$vptServer}/user/${$userUrn}/configuration/update`, {
            method:  'PUT',
            headers: {
                "Authorization": "Bearer " + $token,
                'Content-Type': 'text/plain'
            },
            body: ""
        });
        userFilters.set("");
    }

    function clearAllFilters() {
        keyworkerFilterString = "";
        statusFilterString = "";
        ethnicityFilterString = "";
        nationalityFilterString = "";
        religionFilterString = "";
        unitFilterString = "";
        wingFilterString = "";
        cellStatusFilterString = "";
        regimeFilterString = "";
        ageRangeFilterString = "";
        earlyDaysFilter = false;

        $referenceDataKeyworkerList.forEach(li => {
            li.checked = false;
        });
        $referenceDataStatusList.forEach(li => {
            li.checked = false;
        });
        $referenceDataEthnicityList.forEach(li => {
            li.checked = false;
        })
        $referenceDataNationalityList.forEach(li => {
            li.checked = false;
        })
        $referenceDataReligionList.forEach(li => {
            li.checked = false;
        })
        $referenceDataUnitList.forEach(li => {
            li.checked = false;
        });
        $referenceDataWingList.forEach(li => {
            li.checked = false;
        })
        $referenceDataCellStatusList.forEach(li => {
            li.checked = false;
        })
        $referenceDataRegimeLevelList.forEach(li => {
            li.checked = false;
        })
        $referenceDataAgeRangeList.forEach(li => {
            li.checked = false;
        })

        $referenceDataKeyworkerList = $referenceDataKeyworkerList;
        $referenceDataStatusList = $referenceDataStatusList;
        $referenceDataEthnicityList = $referenceDataEthnicityList;
        $referenceDataNationalityList = $referenceDataNationalityList;
        $referenceDataReligionList = $referenceDataReligionList;
        $referenceDataUnitList = $referenceDataUnitList;
        $referenceDataWingList = $referenceDataWingList;
        $referenceDataCellStatusList = $referenceDataCellStatusList;
        $referenceDataRegimeLevelList = $referenceDataRegimeLevelList; 
        $referenceDataAgeRangeList = $referenceDataAgeRangeList;
    }
</script>

<style>
    .cohort-filters .form-inline {
        height: 35px;
    }

    .cohort-filters {
        position: fixed;
        width: 100%;
        min-width: 1495px;
        top: 50px;
        z-index: 1028;
        padding: 10px 15px 0 15px;
        background-color: #f5f5f5;
    }

    .clear-bar {
        display: flex;
        padding: 0;
        height: 34.6px;
        margin-bottom: 0; 
        width: 12pc;
        align-items: center;
        justify-content: center;
    }

    .filter-inactive {
        background-color: #ffffff;
        color: #212529;
        border: 0;
    }

    .clear-img {
        height: 16px;
        margin-bottom: 2px;
    }

    .clear-btn {
        min-width: 25px;
        padding: 2px 3px 2px 3px;
    }

    .clear-btn:hover {
        background-color: #80BEFF;
    }

    .btn-link {
        padding: 0;
    }

    .btn-link img {
        height: 22px;
    }
</style>

<DatePicker on:datechange={selectReportDate} selected={selectedDate} 
    isAllowed={date => {
        const millisecs = date.getTime();
        if (millisecs + 25 * 3600 * 1000 < Date.now()) return true;
        if (millisecs > Date.now()) return false;
        return true;
    }}/>

<Config />
<Help />

<div class="cohort-filters">
    <div class="form-inline" role="toolbar" aria-label="Cohort filters">
        <div class="alert alert-primary clear-bar mr-4"
            class:filter-inactive={$inmateCount.filtered === $inmateCount.unfiltered}>
            {#if areListsLoading === false}
                {#if $inmateCount.filtered !== $inmateCount.unfiltered}
                    <span class="mr-1">{$inmateCount.filtered} of {$inmateCount.unfiltered} {$config.inmateLabelName}{#if $inmateCount.unfiltered !== 1}s{/if}</span>
                    <button class="btn clear-btn" id="clear-btn" on:click={clearAllFilters}>
                        <img src="/assets/x-circle-fill.svg"  class="clear-img" alt="Clear filters" />
                    </button>
                    <Tooltip target="clear-btn" placement="bottom">Clear all filters</Tooltip>
                {:else}
                    <span>{$inmateCount.unfiltered} {$config.inmateLabelName}{#if $inmateCount.unfiltered !== 1}s{/if}</span>
                {/if}
            {:else}
                <span>Loading...</span>
            {/if}
        </div>    

        <FilterButton on:click={() => {earlyDaysFilter = !earlyDaysFilter}} id="filter-early-days"
                      isActive={earlyDaysFilter} classes={"mr-3"}
                      title="Show only {$config.inmateLabelName.toLowerCase()}s who are within the Early Days period ({$config.numberOfDaysConsideredEarlyDaysPeriod.toString()} {$config.numberOfDaysConsideredEarlyDaysPeriod == 1 ? 'day' : 'days'})">
            Early Days
        </FilterButton>

        {#if $referenceDataKeyworkerList.length > 1}
            <SelectableList list={$referenceDataKeyworkerList} label={$config.keyworkerLabelName} idProperty={'staffUrn'} nameProperty={'name'}
                on:close={(event) => { keyworkerFilterString = event.detail.selectedItems; }} classes={"mr-3"} />
        {/if}

        <SelectableList list={$referenceDataStatusList} label={'Status'} idProperty={'description'} nameProperty={'description'} iconProperty={'iconName'}
            on:close={(event) => { statusFilterString = event.detail.selectedItems; }} classes={"mr-2"} />

        <SelectableList list ={$referenceDataRegimeLevelList} label={'Regime'} idProperty={'level'} nameProperty={'description'}
            on:close={(event) => {regimeFilterString = event.detail.selectedItems; }} classes={"mr-2"} />

        {#if $referenceDataUnitList.length > 1}
            <SelectableList list ={$referenceDataUnitList} label={'Unit'} idProperty={'description'} nameProperty={'description'}
                on:close={(event) => {unitFilterString = event.detail.selectedItems; }} classes={"mr-2"} />
        {/if}

        <SelectableList list ={$referenceDataWingList} label={'Wing'} idProperty={'description'} nameProperty={'description'}
            on:close={(event) => {wingFilterString = event.detail.selectedItems; }} classes={"mr-2"} />

        <SelectableList list ={$referenceDataCellStatusList} label={'Cell Status'} idProperty={'vptCellStatusUrn'} nameProperty={'description'} iconProperty={'iconName'}
            on:close={(event) => {cellStatusFilterString = event.detail.selectedItems; }} classes={"mr-3"} />

        <SelectableList list ={$referenceDataAgeRangeList} label={'Age'} idProperty={'code'} nameProperty={'description'}
            on:close={(event) => {ageRangeFilterString = event.detail.selectedItems; }} classes={"mr-2"} />

        <SelectableList list={$referenceDataEthnicityList} label={'Ethnicity'} idProperty={'code'} nameProperty={'description'}
            on:close={(event) => { ethnicityFilterString = event.detail.selectedItems; }} classes={"mr-2"} />

        <SelectableList list={$referenceDataNationalityList} label={'Nationality'} idProperty={'description'} nameProperty={'description'}
            on:close={(event) => { nationalityFilterString = event.detail.selectedItems; }} classes={"mr-2"} />

        {#if $config.showReligionFilter}
            <SelectableList list={$referenceDataReligionList} label={'Religion'} idProperty={'description'} nameProperty={'description'}
                on:close={(event) => { religionFilterString = event.detail.selectedItems; }} />
        {/if}

        {#if createRequestParams(requestParams) || $userFilters}
            {#if $userFilters == createRequestParams(requestParams)}
                <button id="clear-favourite" on:click="{clearUserFilters}" type="button" class="btn btn-link ml-2 focus-outline">
                    <img src="/assets/star-filled.svg" alt="Restore default view to the whole establishment" />
                </button>
                {#if showFavouriteTooltips}
                    <Tooltip target="clear-favourite" placement="bottom">Restore default view to the whole establishment</Tooltip>
                {/if}
            {:else}
                <button id="save-favourite" on:click="{saveUserFilters}" type="button" class="btn btn-link ml-2 focus-outline">
                    <img src="/assets/star.svg" alt="Save as default view" />
                </button>
                {#if showFavouriteTooltips}
                    <Tooltip target="save-favourite" placement="bottom">Save as default view</Tooltip>
                {/if}
            {/if}
        {/if}  
    </div>
</div> 

<AimToast {...toastProperties}></AimToast>